import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '../../../component/ui/Button';
import InputField from '../../../component/form/FormInput';
import Table from '../../../component/table/Table';
import Pagination from '../../../component/pagination';
import DataNotFound from '../../components/dataNotFound';
import { getListDetailsTableColumns } from './components/ListDetailsTableColumn';
import AddProduct from './AddProduct';
import { toast } from 'react-toastify';
import { getProductListDetails, updateProductList } from '../../../store/action/inventory';
import './List.scss';

const ListDetails = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const suburl = localStorage.getItem('suburl') || 'inventory';

	const [listName, setListName] = useState('');
	const [description, setDescription] = useState('');
	const [assignedProducts, setAssignedProducts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isAddProductsOpen, setIsAddProductsOpen] = useState(false);
	const [, setSelectedRows] = useState([]);
	const [sortDirection, setSortDirection] = useState({ PRODUCTNAME: 'asc' });

	const [params, setParams] = useState(() => ({
		page: Number(searchParams.get('page')) || 1,
		pageSize: Number(searchParams.get('pageSize')) || 20,
	}));

	const totalPages = Math.ceil(assignedProducts.length / params.pageSize) || 1;

	const fetchListDetailsData = useCallback(async () => {
		if (!id) return;
		setLoading(true);
		try {
			const res = await getProductListDetails(id);
			console.log('getProductListDetails Response:', res);

			const details = res?.data?.details || res?.details || res?.data;
			if (details) {
				setListName(details.name || '');
				setDescription(details.description || '');

				const rawProducts = details.associated_products || details.products || [];
				const mappedProducts = rawProducts.map((prod) => ({
					id: prod.product_id || prod.id,
					PRODUCTNAME: prod.product_name || prod.name || prod.PRODUCTNAME,
					UPC: prod.product_code || prod.UPC || prod.upc || '-',
					CATEGORY: prod.category_name || prod.category || prod.CATEGORY || '-',
					'AVAILABLE STOCK': prod['AVAILABLE STOCK'] || `${prod.stock_in_hand ?? 0} ${prod.unit_name || 'units'}`,
					'UNIT PRICE': prod['UNIT PRICE'] || prod.unit_price || prod.sales_price || '0',
				}));
				setAssignedProducts(mappedProducts);
			}
		} catch (err) {
			console.error('Failed to fetch list details:', err);
			toast.error('Failed to load list details');
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		fetchListDetailsData();
	}, [fetchListDetailsData]);

	const handleCloseDetails = () => navigate(`/user/${suburl}/lists`);

	const handleSaveList = async () => {
		if (!id) return;
		try {
			setLoading(true);
			const payload = {
				name: listName.trim(),
				description: description.trim(),
				status: 1,
				product_ids: assignedProducts.map((p) => p.id || p.product_id),
			};
			const res = await updateProductList(id, payload);
			if (res?.success) {
				toast.success(res?.message || 'Product List updated successfully');
				navigate(`/user/${suburl}/lists`);
			} else {
				toast.error(res?.message || res?.response?.data?.message || 'Failed to update list');
			}
		} catch (err) {
			console.error('Update List Error:', err);
			toast.error('Failed to update list');
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveProduct = (productId) => {
		setAssignedProducts((prev) => prev.filter((item) => item.id !== productId));
		toast.success('Product removed from list');
	};

	const handleAddProducts = (newProducts) => {
		if (!newProducts?.length) return;
		const formatted = newProducts.map((item, idx) => ({
			id: item.id || Date.now() + idx,
			PRODUCTNAME: item.name || item.product_name,
			UPC: item.code || item.product_code || item.UPC || '-',
			CATEGORY: item.category_name || item.CATEGORY || '-',
			'AVAILABLE STOCK': `${item.stock_in_hand ?? 0} ${item.unit_name || 'units'}`,
			'UNIT PRICE': item.unit_price || item.sales_price || '0',
		}));
		setAssignedProducts((prev) => [...formatted, ...prev]);
		toast.success('Products added to list successfully');
	};

	const handleSort = (fieldKey) => {
		const currentSort = sortDirection[fieldKey] === 'asc' ? 'desc' : 'asc';
		setSortDirection({ [fieldKey]: currentSort });
		setAssignedProducts((prev) =>
			[...prev].sort((a, b) => {
				let valA = a[fieldKey] ?? '';
				let valB = b[fieldKey] ?? '';
				if (typeof valA === 'number' && typeof valB === 'number') {
					return currentSort === 'asc' ? valA - valB : valB - valA;
				}
				valA = String(valA).toLowerCase();
				valB = String(valB).toLowerCase();
				if (valA < valB) return currentSort === 'asc' ? -1 : 1;
				if (valA > valB) return currentSort === 'asc' ? 1 : -1;
				return 0;
			})
		);
	};

	const columns = useMemo(
		() => getListDetailsTableColumns(handleSort, sortDirection, handleRemoveProduct),
		[sortDirection]
	);

	return (
		<div className="list-details-page-wrapper d-flex flex-column h-100 overflow-hidden" data-testid="product-list-detail-page">
			{/* Top Header Bar */}
			<div className="product-pg-header px-3 py-2 mb-3 d-flex justify-content-between align-items-center">
				<h4 className="fw-bold mb-0 text-dark" data-testid="product-list-detail-title">List Details</h4>
				<div className="d-flex align-items-center gap-2">
					<Button
						type="button"
						button="Save"
						className="btn-primary px-4 mt-0"
						onClick={handleSaveList}
						dataTestId="product-list-detail-save-button"
					/>
					<button
						type="button"
						className="btn btn-danger btn-header-close d-flex align-items-center justify-content-center"
						onClick={handleCloseDetails}
						title="Close Details"
						data-testid="product-list-detail-close-button"
					>
						<span className="fw-bold fs-5">✕</span>
					</button>
				</div>
			</div>

			{/* Metadata & Controls Section */}
			<div className="px-3">
				<div className="row g-3 mb-3">
					<div className="col-md-3 col-sm-6">
						<InputField
							label="List Name"
							type="text"
							name="listName"
							value={listName}
							onChange={(val) => setListName(typeof val === 'string' ? val : val?.target?.value || '')}
							placeholder="Enter List Name"
							dataTestId="product-list-detail-name-input"
						/>
					</div>
					<div className="col-md-4 col-sm-6">
						<InputField
							label="Description"
							type="text"
							name="description"
							value={description}
							onChange={(val) => setDescription(typeof val === 'string' ? val : val?.target?.value || '')}
							placeholder="Enter Description"
							dataTestId="product-list-detail-description-input"
						/>
					</div>
				</div>

				<div className="d-flex justify-content-between align-items-center mb-2">
					<h5 className="fw-bold mb-0 text-dark">
						Assigned Products
					</h5>
					<Button
						add
						type="button"
						button="Add Products"
						className="btn-primary px-3 mt-0"
						onClick={() => setIsAddProductsOpen(true)}
						dataTestId="product-list-detail-add-products-button"
					/>
				</div>
			</div>

			{/* Full Width Edge-to-Edge Table Content */}
			{loading ? (
				<div className="d-flex justify-content-center align-items-center flex-grow-1" data-testid="product-list-detail-loading-state">
					<span className="Page_loader" role="status" aria-hidden="true"></span>
				</div>
			) : assignedProducts.length > 0 ? (
				<div className="flex-grow-1 d-flex flex-column w-100" data-testid="product-list-detail-table">
					<div className="productTable-body flex-grow-1">
						<Table
							row={assignedProducts}
							coloum={columns}
							className="productTable list-details-table"
							isMultiSelect={true}
							action={setSelectedRows}
						/>
					</div>
					<Pagination
						currentPage={params.page}
						totalPages={totalPages}
						onChangePage={(newPage) => setParams((prev) => ({ ...prev, page: newPage }))}
						pageSize={params.pageSize}
						setParam={(value) => setParams((prev) => ({ ...prev, pageSize: value, page: 1 }))}
					/>
				</div>
			) : (
				<div className="px-3" data-testid="product-list-detail-empty-state">
					<DataNotFound
						title={'No assigned products found!'}
						subtitle={'Add products to this list to organize your inventory.'}
						descriptionTitle={'Keep your inventory organized!'}
						descriptionSubTitle={'Assign products to this list easily.'}
						buttonText={'Add Products'}
						onClick={() => setIsAddProductsOpen(true)}
					/>
				</div>
			)}

			{/* Add Products Modal */}
			{isAddProductsOpen && (
				<AddProduct
					isOpen={isAddProductsOpen}
					onClose={() => setIsAddProductsOpen(false)}
					onAddProducts={handleAddProducts}
				/>
			)}
		</div>
	);
};

export default ListDetails;
